import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  Page,
  UnreadUserNotificationCount,
  UserNotification,
  UserNotificationListItem,
} from '@models';
import { BYPASS_AUTH_RECOVERY, HttpService } from '../http.service';
import { pollResumingAfterTokenRefresh } from '../poll-resuming-after-token-refresh';
import { RefreshTokenStateService } from '../refresh-token-state.service';
import { UserNotificationsService } from './user-notifications.service';

export const UNREAD_NOTIFICATIONS_POLL_INTERVAL_MS = 5000;

const UNREAD_COUNT_TIMEOUT_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class UserNotificationsApiService
  extends HttpService<never>
  implements UserNotificationsService
{
  protected override readonly collectionPath = '/notifications';

  private readonly refreshTokenState = inject(RefreshTokenStateService, {
    optional: true,
  });

  private readonly bypassAuthRecoveryContext = new HttpContext().set(
    BYPASS_AUTH_RECOVERY,
    true
  );

  getNotifications(
    pageNumber: number,
    pageSize: number,
    searchText?: string
  ): Observable<Page<UserNotificationListItem>> {
    return this.http.get<Page<UserNotificationListItem>>(this.apiUrl, {
      params: this.getHttpParameters({
        pageNumber,
        pageSize,
        searchText: searchText || undefined,
      }),
    });
  }

  getNotification(id: string): Observable<UserNotification> {
    return this.http.get<UserNotification>(
      `${this.apiUrl}/${encodeURIComponent(id)}`
    );
  }

  markRead(id: string): Observable<UserNotification> {
    return this.http.post<UserNotification>(
      `${this.apiUrl}/${encodeURIComponent(id)}/read`,
      {}
    );
  }

  watchUnreadCount(): Observable<number> {
    return pollResumingAfterTokenRefresh(
      UNREAD_NOTIFICATIONS_POLL_INTERVAL_MS,
      passive => this.getUnreadCount(passive),
      this.refreshTokenState
    );
  }

  private getUnreadCount(passive: boolean): Observable<number> {
    return this.http
      .get<UnreadUserNotificationCount>(`${this.apiUrl}/unread-count`, {
        timeout: UNREAD_COUNT_TIMEOUT_MS,
        context: passive ? this.bypassAuthRecoveryContext : undefined,
      })
      .pipe(map(response => response.count));
  }
}
